import { auth } from '../firebase/config';

class PlayerDetectionService {
  constructor() {
    // Check if we're in development by looking at the URL
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    // Check for URL parameter override (e.g., ?useRemoteAPIs=true)
    const urlParams = new URLSearchParams(window.location.search);
    const useRemoteAPIs = urlParams.get('useRemoteAPIs') === 'true';
    
    const envSuffix = import.meta.env.PROD ? 'prod' : 'dev';
    this.apiUrl = (isDevelopment && !useRemoteAPIs)
      ? 'http://localhost:8002'  // Local player detector API
      : `https://player-detector-api-${envSuffix}.blackbay-6bd764e8.eastus2.azurecontainerapps.io`;
  }

  /**
   * Extract frame data from video element as base64 for API transmission
   */
  extractFrameAsBase64(sourceElement) {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas to original size — works for both <video> and <canvas> sources
      const w = sourceElement.videoWidth || sourceElement.width;
      const h = sourceElement.videoHeight || sourceElement.height;
      canvas.width = w;
      canvas.height = h;
      
      // Draw current frame
      ctx.drawImage(sourceElement, 0, 0);
      
      // Convert to base64 JPEG (smaller than PNG for transmission)
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      
      // Extract base64 data without the data:image/jpeg;base64, prefix
      const base64Data = dataUrl.split(',')[1];
      
      return base64Data;
    } catch (error) {
      console.error('❌ Frame extraction failed:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      if (error.name === 'SecurityError' || error.message.includes('insecure') || error.message.includes('CORS')) {
        throw new Error('Cannot extract video frame data due to CORS restrictions. Please ensure the video has proper CORS headers or is served from the same origin.');
      }
      throw error;
    }
  }

  /**
   * Detect players in video frame using server-side API
   */
  async detect(sourceElement, threshold = 0.5) {
    try {
      // Extract frame as base64
      const imageData = this.extractFrameAsBase64(sourceElement);

      // Get Firebase ID token
      const user = auth.currentUser;
      const idToken = user ? await user.getIdToken() : null;

      const headers = { 'Content-Type': 'application/json' };
      if (idToken) {
        headers['X-Firebase-Token'] = idToken;
      }
      
      // Call the API
      const apiEndpoint = `${this.apiUrl}/api/predict`;
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          image_data: imageData,
          threshold: threshold
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API call failed: ${response.status} - ${errorText.substring(0, 200)}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Player detection failed');
      }

      return result.players;
      
    } catch (error) {
      console.error('Player detection failed:', error);
      throw error;
    }
  }

  /**
   * Health check for the player detector API
   */
  async healthCheck() {
    try {
      const response = await fetch(`${this.apiUrl}/health`);
      if (!response.ok) {
        throw new Error(`Health check failed: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Player detector API health check failed:', error);
      throw error;
    }
  }

  /**
   * Filter player detections to get top and bottom players.
   * Uses court position and distance to baseline logic from player_filtering.py
   * 
   * @param {Array} players - Array of player detections with x1, y1, x2, y2, confidence
   * @param {Function} pixelToMetersFn - Function that converts pixel coordinates to meters
   * @param {number} videoWidth - Video width in pixels
   * @param {number} videoHeight - Video height in pixels
   * @returns {Object} Object with 'top' and 'bottom' player detections, or null for missing players
   */
  async filterPlayers(players, pixelToMetersFn, videoWidth, videoHeight) {
    // Constants from splitstep_process.utils.constants
    const BASELINE = 23.77; // meters
    const SIDELINE = 10.97; // meters
    
    if (!players || players.length === 0) {
      return { top: null, bottom: null };
    }
    
    // Calculate player feet position and convert to real-world coordinates
    const playersWithPosition = await Promise.all(players.map(async (player) => {
      // Calculate feet position: ((x1+x2)/2, y2)
      const feetX = (player.x1 + player.x2) / 2;
      const feetY = player.y2;
      
      // Convert to meters using the pixel-to-meters function
      const realWorldCoords = await pixelToMetersFn(feetX, feetY);
      
      if (!realWorldCoords) {
        return null;
      }
      
      return {
        ...player,
        feetX,
        feetY,
        x_m: realWorldCoords.x,
        y_m: realWorldCoords.y,
        z_m: realWorldCoords.z || 0
      };
    }));
    
    // Filter out null values (conversion failures)
    const validPlayers = playersWithPosition.filter(p => p !== null);
    
    if (validPlayers.length === 0) {
      return { top: null, bottom: null };
    }
    
    // Apply court position filter (from player_filtering.py lines 32-35)
    const filteredPlayers = validPlayers.filter(player => {
      const condition1 = Math.abs(player.x_m) < BASELINE / 2 + 1;
      const condition2 = (Math.abs(player.x_m) < BASELINE / 2 + 2) && (Math.abs(player.y_m) > SIDELINE / 2 - 1);
      return condition1 || condition2;
    });
    
    if (filteredPlayers.length === 0) {
      return { top: null, bottom: null };
    }
    
    // Calculate distance to baseline and player position (from player_filtering.py lines 37-40)
    const playersWithDistance = filteredPlayers.map(player => ({
      ...player,
      d_to_baseline: Math.sqrt(
        Math.pow(player.x_m - 0, 2) + Math.pow(Math.abs(player.y_m) - SIDELINE / 2, 2)
      ),
      player_position: player.y_m < 0 ? 'bottom' : 'top'
    }));
    
    // Sort by distance and group by position to get closest player to each baseline
    const topPlayers = playersWithDistance.filter(p => p.player_position === 'top');
    const bottomPlayers = playersWithDistance.filter(p => p.player_position === 'bottom');
    
    // Get the closest player to each baseline
    const topPlayer = topPlayers.length > 0 
      ? topPlayers.sort((a, b) => a.d_to_baseline - b.d_to_baseline)[0]
      : null;
    
    const bottomPlayer = bottomPlayers.length > 0
      ? bottomPlayers.sort((a, b) => a.d_to_baseline - b.d_to_baseline)[0]
      : null;
    
    // Remove the distance property before returning (it was just for filtering)
    const cleanPlayer = (player) => {
      if (!player) return null;
      const { d_to_baseline, player_position, ...cleanedPlayer } = player;
      return {
        ...cleanedPlayer,
        position: player_position // Keep position as 'top' or 'bottom'
      };
    };
    
    return {
      top: cleanPlayer(topPlayer),
      bottom: cleanPlayer(bottomPlayer)
    };
  }
}

// Export singleton instance
export const playerDetectionService = new PlayerDetectionService();

